import React from "react";
import { Box, Typography, Avatar, Grid, ListItem, List } from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from 'moment';
import RectangleIcon from "@mui/icons-material/Rectangle";

interface SolutionOwnerInfo {
    id: string;
    name: string;
    email?: string;
    googleProfilePicture?: string | null;
}

// Interface for the table, matching the data from RAGPage
interface RAG {
    id: string;
    name: string;
    status: 'pending' | 'active' | 'rejected' | string;
    projectId: string;
    databaseType: string;
    embeddingModelId: string;
    applicationName?: string;
    cloudProvider: string;
    solutionOwner?: SolutionOwnerInfo | null;
    createdAt: string;
}

interface RAGsTableProps {
    rags: RAG[];
    columnFilters: Record<string, boolean>;
    statusType?: 'pending' | 'active' | 'rejected';
}

const RAGsTable: React.FC<RAGsTableProps> = ({
    rags,
    columnFilters,
    statusType,
}) => {
    const [cardHoverIndex, setCardHoverIndex] = React.useState<number | null>(null);
    const navigate = useNavigate();

    
    const handleRowClick = (ragId: string) => {
        navigate(`/aisolutions/rag/details/${ragId}`); // Navigate to RAG details page
    };

    const dateFormatter = (dateValue: string | number | null | undefined) => {
            if (!dateValue) return null;
            const numericDate = Number(dateValue);
            if (isNaN(numericDate)) return null;
            return moment(new Date(numericDate).toISOString()).format("DD/MM/YYYY | hh:mm A");
        };

    const headers = [
        { key: "name", label: "RAG NAME", xs: 2 },
        { key: "projectId", label: "CLOUD IDENTIFER", xs: 2 },
        { key: "databaseType", label: "DATABASE TYPE", xs: 2 },
        { key: "embeddingModelId", label: "EMBEDDING MODEL", xs: 1.5 },
        { key: "cloudProvider", label: "CLOUD PROVIDER", xs: 1.5 },
        { key: "solutionOwner", label: "RESOURCE OWNER", xs: 1.5 },
        { key: "createdAt", label: "ADDED ON", xs: 1.5 },
    ];

    const iconColor = statusType === "active" ? "#12B76A" : statusType === "rejected" ? "#F04438" : "#FDB022";
    const listItemBgColor = statusType === "active" ? "#FFFFFF" : statusType === "rejected" ? "#F0443810" : "#FDB02210";


    return (
        <List sx={{ bgcolor: "transparent", px: 0, mx: 0, width: '100%' }}>
            <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px", mb: 0.5, px: 2, py: 1, width: '100%' }}>
                <Box width="100%">
                    <Grid container direction="row" alignItems="center">
                        {headers.map(header => (
                            columnFilters[header.key] && (
                                <Grid item xs={header.xs as any} key={header.key}>
                                    <Typography fontSize="12px" color="#000000" fontFamily="Gilroy-Medium">
                                        {header.label}
                                    </Typography>
                                </Grid>
                            )
                        ))}
                    </Grid>
                </Box>
            </ListItem>

            {rags.map((rag, index) => (
                <ListItem
                    key={rag.id}
                    sx={{
                        mt: 1,
                        borderRadius: "8px",
                        cursor: "pointer",
                        px: 2,
                        width: '100%',
                        backgroundColor: listItemBgColor,
                        '&:hover': {
                            borderColor: "black",
                        }
                    }}
                    onMouseEnter={() => setCardHoverIndex(index)}
                    onMouseLeave={() => setCardHoverIndex(null)}
                    onClick={() => handleRowClick(rag.id)}
                >
                    <Box width="100%">
                        <Grid container direction="row" alignItems="center">
                            {columnFilters.name && (
                                <Grid item xs={headers.find(h => h.key === 'name')?.xs as any}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <RectangleIcon sx={{ color: iconColor, bgcolor: iconColor, width: "4px", height: "22px", marginRight: "8px" }} />
                                        <Typography fontSize="14px" fontFamily="Gilroy-Medium" color="text.primary" noWrap>
                                            {rag.name || "N/A"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {columnFilters.projectId && (
                                <Grid item xs={headers.find(h => h.key === 'projectId')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {rag.projectId || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                             {columnFilters.databaseType && (
                                <Grid item xs={headers.find(h => h.key === 'databaseType')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {rag.databaseType || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.embeddingModelId && (
                                <Grid item xs={headers.find(h => h.key === 'embeddingModelId')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {rag.embeddingModelId || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                             {columnFilters.cloudProvider && (
                                <Grid item xs={headers.find(h => h.key === 'cloudProvider')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" sx={{ textTransform: 'uppercase' }}>
                                        {rag.cloudProvider || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.solutionOwner && (
                                <Grid item xs={headers.find(h => h.key === 'solutionOwner')?.xs as any}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Avatar
                                            src={rag.solutionOwner?.googleProfilePicture || undefined}
                                            alt={rag.solutionOwner?.name || ""}
                                            sx={{ marginRight: "8px", height: 24, width: 24, fontSize: '0.75rem', bgcolor: rag.solutionOwner?.googleProfilePicture ? 'transparent' : 'rgba(0,0,0,0.1)' }}
                                        >
                                            {rag.solutionOwner && !rag.solutionOwner.googleProfilePicture ? rag.solutionOwner.name.charAt(0).toUpperCase() : null}
                                        </Avatar>
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                            {rag.solutionOwner?.name || "N/A"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {columnFilters.createdAt && (
                                <Grid item xs={headers.find(h => h.key === 'createdAt')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
                                        {dateFormatter(Number(rag.createdAt))}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </ListItem>
            ))}
        </List>
    );
};

export default RAGsTable;